// Запросы к серверу заказов. Ошибки делятся на три рода:
// - нет связи: сеть пропала или сервер не ответил (5xx) — экран показывает «Нет связи»;
// - нужен вход: сервер не знает покупателя (401);
// - отказ: сервер объяснил по-русски, почему нельзя (остальные 4xx, поле detail).

export const API_PREFIX = "/api/v1";

export type ApiErrorKind = "network" | "unauthorized" | "not_found" | "refused";

const NETWORK_MESSAGE_RU = "Нет связи. Проверь интернет — всё, что ты уже сделал, сохранилось.";
const INVALID_INPUT_MESSAGE_RU = "Проверь, что всё заполнено правильно.";

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number | null;
  readonly messageRu: string;
  // Поля ответа кроме detail: например, next_free_attempt_at или site_url.
  readonly details: Record<string, unknown>;

  constructor(
    kind: ApiErrorKind,
    status: number | null,
    messageRu: string,
    details: Record<string, unknown> = {},
  ) {
    super(messageRu);
    this.name = "ApiError";
    this.kind = kind;
    this.status = status;
    this.messageRu = messageRu;
    this.details = details;
  }
}

export function isApiError(error: unknown): error is ApiError {
  return error instanceof ApiError;
}

// Токен сессии Mini App живёт только в памяти вкладки. На сайте вход — в httpOnly-cookie.
let miniAppSessionToken: string | null = null;

export function setMiniAppSessionToken(token: string | null): void {
  miniAppSessionToken = token;
}

type RequestOptions = {
  method?: "GET" | "POST" | "PUT" | "DELETE";
  json?: unknown;
  formData?: FormData;
  signal?: AbortSignal;
  headers?: Record<string, string>;
};

async function readBody(response: Response): Promise<unknown> {
  const text = await response.text();
  if (text === "") {
    return null;
  }
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function refusalFrom(status: number, body: unknown): ApiError {
  const fields = typeof body === "object" && body !== null ? { ...(body as Record<string, unknown>) } : {};
  const detail = fields.detail;
  delete fields.detail;
  // FastAPI отвечает на неверный запрос списком ошибок полей: покупателю — общая фраза.
  const messageRu = typeof detail === "string" ? detail : INVALID_INPUT_MESSAGE_RU;
  if (status === 401) {
    return new ApiError("unauthorized", status, messageRu, fields);
  }
  if (status === 404) {
    return new ApiError("not_found", status, messageRu, fields);
  }
  return new ApiError("refused", status, messageRu, fields);
}

export async function apiRequest<ResponseBody>(
  path: string,
  options: RequestOptions = {},
): Promise<ResponseBody> {
  const headers: Record<string, string> = { ...options.headers };
  let body: BodyInit | undefined;
  if (options.json !== undefined) {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify(options.json);
  } else if (options.formData !== undefined) {
    body = options.formData;
  }
  if (miniAppSessionToken !== null) {
    headers.Authorization = `Bearer ${miniAppSessionToken}`;
  }

  let response: Response;
  try {
    response = await fetch(`${API_PREFIX}${path}`, {
      method: options.method ?? "GET",
      headers,
      body,
      credentials: "same-origin",
      signal: options.signal,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") {
      throw error;
    }
    throw new ApiError("network", null, NETWORK_MESSAGE_RU);
  }

  if (response.status >= 500) {
    throw new ApiError("network", response.status, NETWORK_MESSAGE_RU);
  }
  const responseBody = await readBody(response);
  if (!response.ok) {
    throw refusalFrom(response.status, responseBody);
  }
  return responseBody as ResponseBody;
}

// Загрузка файла с долей загрузки (fetch долю не сообщает). Ошибки — те же три рода.
export function apiUpload<ResponseBody>(
  path: string,
  formData: FormData,
  onProgress: (share: number) => void,
): Promise<ResponseBody> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open("POST", `${API_PREFIX}${path}`);
    if (miniAppSessionToken !== null) {
      request.setRequestHeader("Authorization", `Bearer ${miniAppSessionToken}`);
    }
    request.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) {
        onProgress(event.loaded / event.total);
      }
    };
    request.onerror = () => reject(new ApiError("network", null, NETWORK_MESSAGE_RU));
    request.onload = () => {
      if (request.status >= 500) {
        reject(new ApiError("network", request.status, NETWORK_MESSAGE_RU));
        return;
      }
      let body: unknown = null;
      try {
        body = request.responseText === "" ? null : JSON.parse(request.responseText);
      } catch {
        body = request.responseText;
      }
      if (request.status < 200 || request.status >= 300) {
        reject(refusalFrom(request.status, body));
        return;
      }
      resolve(body as ResponseBody);
    };
    request.send(formData);
  });
}
